import { extendClass } from './extend-class'
import { DateTime } from 'luxon'
import { SeDateTimeFormatter } from './date-time-formatter'
import { SeDate } from './date'
import { SeTime } from './time'
import {
  SeDateTimeString,
  SeDateTimeOptions,
  SeDateTimeRangeValue,
  SeDateTimeChunk,
  SeDateTimeFormat,
  SeDateTimeModifier,
} from './date-time-types'

const CHUNK: RegExp = /(\^|\$)(\([^\)]+\))|([^(\^|\$)]+)/g // `foo^(start)bar$(end)baz` -> ['foo', 'start', 'bar', 'end', 'baz']
const RANGE_WRAPPER: RegExp = /^(\^|\$)\(|\)$/g // matches `$(` at the start `^(` and `)` at the end

export abstract class SeRangeFormatter<TPointFormatter extends SeDate | SeTime> {

  public start: TPointFormatter
  public end: TPointFormatter

  public initRange(
    startValue: SeDateTimeString,
    endValue: SeDateTimeString,
    options: SeDateTimeOptions,
    pointFormatter: { new(value: SeDateTimeString, options: SeDateTimeOptions): TPointFormatter }
  ): void {
    const start = (this.start = new pointFormatter(startValue, options)).luxon
    const end = (this.end = new pointFormatter(endValue, options)).luxon

    this.findChunks = this._findChunks
    this.isValid = start.isValid && (!endValue || end.isValid)
    this.isValidRange = start.isValid && end.isValid && (+end > +start) && !start.hasSame(end, 'minute')

    this.addConditions([
      { [SeDateTimeFormat.DifferentDays]: () => this.differentDays },
      { [SeDateTimeFormat.DifferentMonths]: () => this.differentMonths },
      { [SeDateTimeFormat.DifferentYears]: () => this.differentYears },
      { [SeDateTimeFormat.NonCurrentYear]: () => this.nonCurrentYear },
    ])
  }

  // finds range chunks, mapping any not enclosed in a range to the start value
  private _findChunks(format: string): SeDateTimeChunk[] {
    const chunks = format.match(CHUNK) || []
    return chunks.map(match => {
      const format = match.replace(RANGE_WRAPPER, '')
      const endRange = match !== format && match[0] === '$'
      const luxon = endRange ? this.end.luxon : this.start.luxon
      return { format, luxon }
    })
  }

  private buildChunk(format: string, luxon: DateTime): SeDateTimeChunk {
    return { format, luxon }
  }

  public get value(): SeDateTimeRangeValue {
    return {
      starts_at: this.start.value,
      ends_at: this.end.value
    }
  }
}

export interface SeRangeFormatter<TPointFormatter extends SeDate | SeTime> extends SeDateTimeFormatter {}
extendClass(SeRangeFormatter, SeDateTimeFormatter)
